import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Wrench } from "lucide-react";
import type { CoreWebVitalsDetails, PageIssue } from "@/types/technicalAudit";

interface CoreWebVitalsCardProps {
	issue: PageIssue;
}

// core_web_vitals: a failing Core Web Vitals metric (e.g. LCP). Improving it is
// performance work, not a safe auto-apply — surface a clear recommendation.
// Mirrors MissingCompressionCard.
export function CoreWebVitalsCard({ issue }: CoreWebVitalsCardProps) {
	const details = (issue.issue_details || {}) as Partial<CoreWebVitalsDetails>;
	const fix = issue.proposed_fix as { recommendation?: string } | null;
	const metric = details.metric || "Metric";
	const value = typeof details.value === "number" ? details.value : null;
	const threshold =
		typeof details.threshold === "number" ? details.threshold : null;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border border-coral/40 bg-coral-light">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-coral/10">
					<AlertTriangle className="w-5 h-5 text-coral" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-coral-dark">
						Core Web Vitals: {metric} over threshold
					</h4>
					<p className="text-sm mt-1 text-coral-dark break-all">
						{details.page_url || issue.page_url}
					</p>
				</div>
			</div>

			<div className="grid grid-cols-2 gap-3">
				<div className="rounded-lg border border-coral/30 bg-coral-light p-3">
					<p className="text-xs uppercase tracking-wide text-coral-dark">
						{metric} value
					</p>
					<p className="mt-1 text-lg font-semibold text-coral-dark">
						{value ?? "—"}
					</p>
				</div>
				<div className="rounded-lg border border-border bg-card p-3">
					<p className="text-xs uppercase tracking-wide text-muted-foreground">
						Threshold
					</p>
					<p className="mt-1 text-lg font-semibold text-foreground">
						{threshold ?? "—"}
					</p>
				</div>
			</div>

			<Card className="border-teal/30 shadow-sm bg-teal-light/40">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-teal-dark flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-teal/15 flex items-center justify-center">
							<Wrench className="w-4 h-4 text-teal-dark" />
						</div>
						Recommended Action
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					<div className="p-3 bg-card rounded-lg border border-teal/30">
						<p className="text-sm text-teal-dark font-medium">
							Improve the {metric} metric for this page.
						</p>
						<p className="text-sm text-teal-dark mt-1">
							Investigate the failing metric — optimize the largest element,
							reduce layout shift, or trim main-thread work — and re-measure.
							{fix?.recommendation
								? ` ${fix.recommendation}`
								: details.recommendation
									? ` ${details.recommendation}`
									: ""}
						</p>
					</div>
					<div className="flex items-center gap-2">
						<Badge
							variant="outline"
							className="border-teal/40 bg-teal-light text-teal-dark"
						>
							Page-level fixes auto-applied
						</Badge>
					</div>
					<p className="text-xs text-muted-foreground">
						Apply enables the AgileCO CWV page optimizer for this page: image
						dimensions are injected (CLS) and below-the-fold images lazy-load
						with the hero image protected (LCP). Hosting/TTFB, text-only LCP,
						and heavy theme JavaScript still need manual performance work.
					</p>
				</CardContent>
			</Card>
		</div>
	);
}
